/**
 * The bell: how many are unread, and the list one click away.
 *
 * Polled once a minute, and whenever the window regains focus — a reminder that shows
 * up only after a reload has already missed its moment. The list is written by the
 * server in the language on screen, so switching language rewrites it.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useI18n } from '@/i18n';
import { api, type AppNotification } from '@/lib/api';

import { portalLink, relativeTime } from './format';

function BellIcon() {
  return (
    <svg
      viewBox="0 0 20 20"
      aria-hidden="true"
      className="w-4 h-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 8.2a5 5 0 0 1 10 0c0 4 1.6 5.6 1.6 5.6H3.4S5 12.2 5 8.2Z" />
      <path d="M8.3 16.4a1.9 1.9 0 0 0 3.4 0" />
    </svg>
  );
}

export function NotificationBell({ align = 'left' }: { align?: 'left' | 'right' }) {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  const list = useQuery({
    queryKey: ['notifications', locale],
    queryFn: () => api.notifications(locale),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  const unread = list.data?.unread ?? 0;

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['notifications'] });
  const readOne = useMutation({
    mutationFn: api.readNotification,
    onSuccess: refresh,
  });
  const readAll = useMutation({
    mutationFn: api.readAllNotifications,
    onSuccess: refresh,
  });

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openItem = (n: AppNotification) => {
    if (!n.readAt) readOne.mutate(n.id);
    const to = portalLink(n.link);
    if (to) {
      setOpen(false);
      navigate(to);
    }
  };

  return (
    <div className="relative" ref={box}>
      <button
        type="button"
        aria-label={
          unread > 0
            ? `${t('notifications.bell')} — ${t('notifications.unread', { count: unread })}`
            : t('notifications.bell')
        }
        aria-expanded={open}
        title={t('notifications.bell')}
        onClick={() => setOpen((v) => !v)}
        className="relative w-8 h-8 inline-flex items-center justify-center rounded-full border border-border bg-surface text-subtle transition-colors hover:text-primary hover:border-[var(--color-primary)]"
      >
        <BellIcon />
        {unread > 0 ? (
          <span className="absolute -top-1 -end-1 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-[var(--color-on-primary)] text-[10px] font-bold leading-[18px] text-center tabular">
            {unread > 99 ? '99+' : unread}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label={t('notifications.bell')}
          className={`absolute top-10 z-50 w-[22rem] max-w-[calc(100vw-2rem)] rounded-lg border border-border bg-surface shadow-lg ${
            align === 'left' ? 'start-0' : 'end-0'
          }`}
        >
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border">
            <div className="font-bold text-sm">{t('notifications.bell')}</div>
            {unread > 0 ? (
              <button
                type="button"
                className="text-xs font-semibold text-primary hover:underline"
                onClick={() => readAll.mutate()}
              >
                {t('notifications.markAllRead')}
              </button>
            ) : null}
          </div>

          <div className="max-h-[26rem] overflow-y-auto">
            {(list.data?.notifications ?? []).length === 0 ? (
              <div className="px-4 py-8 text-center">
                <div className="text-sm font-medium">{t('notifications.empty')}</div>
                <div className="text-xs text-muted mt-1">{t('notifications.emptyHint')}</div>
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {list.data!.notifications.map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => openItem(n)}
                      className={`w-full text-start flex gap-3 px-4 py-3 transition-colors hover:bg-surface-alt ${
                        n.readAt ? '' : 'bg-[var(--color-primary-soft)]'
                      }`}
                    >
                      <span
                        aria-hidden="true"
                        className={`mt-1.5 w-2 h-2 shrink-0 rounded-full ${n.readAt ? 'bg-transparent' : 'bg-primary'}`}
                      />
                      <span className="min-w-0 flex-1">
                        <span className={`block text-sm leading-snug ${n.readAt ? 'font-medium' : 'font-semibold'}`}>
                          {n.title}
                        </span>
                        {n.body ? <span className="block text-xs text-muted leading-snug mt-0.5">{n.body}</span> : null}
                        <span className="block text-[11px] text-subtle mt-1">{relativeTime(n.createdAt, locale)}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="px-4 py-2.5 border-t border-border">
            <Link
              to="/settings?tab=notifications"
              onClick={() => setOpen(false)}
              className="text-xs font-semibold text-primary hover:underline"
            >
              {t('notifications.settingsLink')}
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}
